import QRCode from "qrcode";
import { BADGE_PRESETS, encodeBadgeQr } from "./badge-template";

/**
 * Resolve {{group.field}} tokens against badge data source samples (same rules as Badge Designer).
 */
export function resolveBadgeTokens(content, dataSources) {
  if (!content || typeof content !== "string") return content;
  return content.replace(/\{\{([^}]+)\}\}/g, (_, raw) => {
    const token = String(raw || "").trim();
    const [group, key] = token.split(".");
    const source = dataSources?.[group];
    if (!source || !source.sample) return "";
    const value = source.sample?.[key];
    if (value === undefined || value === null || String(value).trim() === "") return "";
    return String(value);
  });
}

export function badgeCanvasPx(template) {
  const preset = BADGE_PRESETS.find((p) => p.id === template?.preset) || BADGE_PRESETS[0];
  const wMm = template?.size?.width ?? preset.widthMm;
  const hMm = template?.size?.height ?? preset.heightMm;
  return {
    w: Math.max(220, Math.round(Number(wMm) * 4.2)),
    h: Math.max(120, Math.round(Number(hMm) * 4.2)),
  };
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function layerToHtml(layer, dataSources) {
  const text = resolveBadgeTokens(layer.content, dataSources);
  const { x, y, w, h } = layer;
  if (layer.hidden) return "";
  if (layer.type === "image") {
    const src = escapeHtml(text);
    return `<img class="b-layer" style="left:${x}px;top:${y}px;width:${w}px;height:${h}px;object-fit:cover" src="${src}" alt="" />`;
  }
  if (layer.type === "shape") {
    return `<div class="b-layer b-shape" style="left:${x}px;top:${y}px;width:${w}px;height:${h}px;background:${layer.fill || "#09090B"};color:${layer.textColor || "var(--primary)"};font-size:${layer.fontSize || 14}px;font-weight:${layer.fontWeight || 600}">${escapeHtml(text)}</div>`;
  }
  if (layer.type === "qr") {
    const src = await QRCode.toDataURL(text || " ", {
      errorCorrectionLevel: "M",
      margin: 0,
      color: { dark: "#09090B", light: "#FFFFFF" },
      width: 512,
    });
    return `<img class="b-layer" style="left:${x}px;top:${y}px;width:${w}px;height:${h}px;object-fit:contain" src="${src}" alt="" />`;
  }
  return `<div class="b-layer b-text" style="left:${x}px;top:${y}px;width:${w}px;min-height:${h}px;font-size:${layer.fontSize || 16}px;font-weight:${layer.fontWeight || 500};line-height:1.05">${escapeHtml(text)}</div>`;
}

/**
 * Merge generated QR payload so {{qr.code}} resolves for print/preview.
 */
export async function mergeQrPayload(eventId, dataSources, subjectType, subjectId) {
  const merged = JSON.parse(JSON.stringify(dataSources || {}));
  const st = subjectType === "staff" ? "staff" : subjectType === "vip" ? "vip" : "attendee";
  let code;
  if (eventId && subjectId) {
    try {
      code = await encodeBadgeQr(eventId, st, subjectId);
    } catch {
      code = `B1|${eventId}|${st}|${subjectId}`;
    }
  } else {
    code = `B1|${eventId || "event"}|${st}|${subjectId || "preview"}`;
  }
  merged.qr = { sample: { code }, fields: ["code"] };
  return merged;
}

/**
 * @param {object} opts
 * @param {boolean} [opts.forModalPreview] — gray chrome, no toolbar (iframe srcDoc).
 * @param {boolean} [opts.includePrintChrome] — dark toolbar + Print button (standalone window).
 */
export async function buildBadgePrintHtml({
  template,
  dataSources,
  eventTitle = "",
  footnote = "",
  forModalPreview = false,
  includePrintChrome = false,
}) {
  const layers = template?.layers || [];
  const canvasPx = badgeCanvasPx(template);
  const parts = [];
  for (const layer of layers) {
    parts.push(await layerToHtml(layer, dataSources));
  }
  const inner = `
  <div class="badge-host" style="position:relative;width:${canvasPx.w}px;height:${canvasPx.h}px;background:#FAFAFA;color:#09090B;">
    <div class="accent-bar" style="position:absolute;top:0;left:0;right:0;height:12px;background:var(--primary);z-index:0;"></div>
    ${parts.join("")}
  </div>`;

  const baseCss = `
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, -apple-system, Segoe UI, sans-serif; }
  .b-layer { position: absolute; overflow: hidden; z-index: 1; }
  .b-shape { display: flex; align-items: center; justify-content: center; font-family: ui-monospace, monospace; text-transform: uppercase; letter-spacing: 0.06em; padding: 4px; text-align: center; }
  .b-text { font-family: "DM Sans", system-ui, sans-serif; }
  @page { size: auto; margin: 14mm; }
  @media print {
    body { background: #fff !important; }
    .no-print { display: none !important; }
    .badge-wrap { padding: 0 !important; }
    .badge-host { box-shadow: none !important; }
  }`;

  if (forModalPreview) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${baseCss}
      body { background: #1a1a1e; display: flex; justify-content: center; align-items: flex-start; padding: 20px; min-height: 100vh; }
      .badge-wrap { filter: drop-shadow(0 24px 48px rgba(0,0,0,0.45)); }
    </style></head><body><div class="badge-wrap">${inner}</div></body></html>`;
  }

  const chrome = includePrintChrome
    ? `<div class="no-print screen-toolbar" style="display:flex;gap:12px;align-items:center;padding:14px 20px;background:#0a0a0b;color:#fafafa;border-bottom:1px solid #27272a;">
        <span style="flex:1;font-size:13px;">${escapeHtml(eventTitle || "Badge")}</span>
        <button type="button" onclick="window.print()" style="padding:10px 18px;font-weight:600;border:none;cursor:pointer;background:var(--primary);color:#09090b;border-radius:8px;font-size:13px;">Print</button>
      </div>`
    : "";

  const foot = footnote ? `<p style="margin:16px 0 0;font-size:11px;color:#71717a;font-family:ui-monospace,monospace;text-align:center;max-width:480px;">${escapeHtml(footnote)}</p>` : "";

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtml(eventTitle || "Badge")}</title><style>${baseCss}
    body { margin: 0; background: #e4e4e7; }
    .badge-outer { display: flex; flex-direction: column; align-items: center; padding: 24px; min-height: 100vh; }
    .badge-wrap { display: flex; flex-direction: column; align-items: center; }
  </style></head><body>
    ${chrome}
    <div class="badge-outer"><div class="badge-wrap">${inner}${foot}</div></div>
  </body></html>`;
}

/**
 * Open a dedicated window and trigger the system print dialog (QR images loaded first).
 */
export function openBadgePrintWindow(html) {
  const win = window.open("", "_blank", "noopener,noreferrer,width=960,height=1100");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  const runPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
  };
  win.addEventListener("load", () => setTimeout(runPrint, 200));
  setTimeout(runPrint, 800);
  return true;
}
